/**
 * Éliminations d'archives — M3 (retour Manon, mi-parcours).
 *
 * « Date de destruction en fonction du type de document, avec édition du
 *   document pour la déclaration aux Archives départementales. »
 *
 * 1. Ce qui est éliminable (DUA échue) et ce qui le devient dans l'année.
 * 2. Cocher → « Préparer le bordereau » → PDF à envoyer aux AD.
 * 3. Le circuit : envoyé → VISA reçu (date, référence) → destruction faite.
 *    Aucune destruction sans visa (Code du patrimoine, art. L212-2 et R212-14) :
 *    le bouton n'apparaît qu'une fois le visa enregistré, et le serveur refuse.
 */
import { useCallback, useEffect, useState } from 'react';
import { FileDown, Send, BadgeCheck, Trash2, X } from 'lucide-react';
import { Card, Button, Input, Badge } from '@/components/ui';
import { api, ApiException, type BordereauDTO, type UniteEliminableDTO } from '@/lib/api';

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');
const dates = (a: number | null, z: number | null) => (a && z ? (a === z ? `${a}` : `${a}–${z}`) : a ?? z ?? '—');
const STATUT: Record<BordereauDTO['statut'], { l: string; v: 'neutral' | 'warning' | 'success' | 'urgent' }> = {
  brouillon: { l: 'Brouillon', v: 'neutral' }, envoye: { l: 'Envoyé aux AD', v: 'warning' },
  vise: { l: 'Visé — à détruire', v: 'success' }, elimine: { l: 'Éliminé', v: 'neutral' }, annule: { l: 'Annulé', v: 'urgent' },
};

export function ArchivesEliminationPanel() {
  const [elim, setElim] = useState<UniteEliminableDTO[]>([]);
  const [aVenir, setAVenir] = useState<UniteEliminableDTO[]>([]);
  const [bordereaux, setBordereaux] = useState<BordereauDTO[]>([]);
  const [choisies, setChoisies] = useState<string[]>([]);
  const [service, setService] = useState('');
  const [saisie, setSaisie] = useState<Record<string, { date?: string; ref?: string; mode?: string }>>({});
  const [err, setErr] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const [e, b] = await Promise.all([api.admin.archives.eliminables(), api.admin.archives.bordereaux()]);
      setElim(e.eliminables); setAVenir(e.aVenir); setBordereaux(b.bordereaux);
    } catch (x) { setErr(x instanceof ApiException ? x.payload.error : 'Chargement impossible'); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  const act = async (f: () => Promise<unknown>) => {
    setErr(null);
    try { await f(); await charger(); } catch (x) { setErr(x instanceof ApiException ? x.payload.error : 'Action impossible'); }
  };
  const libres = elim.filter((u) => !u.surBordereau);
  const maj = (id: string, k: 'date' | 'ref' | 'mode', v: string) => setSaisie((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));

  return (
    <div className="space-y-4">
      {err && <p className="text-sm text-urgent" role="alert">{err}</p>}

      <Card variant="glass" padded>
        <h2 className="text-sm font-semibold text-ink">Éliminables aujourd'hui (DUA échue)</h2>
        <p className="mb-3 text-xs text-ink-muted">
          D'après la DUA et le sort final réglés dans l'onglet Catégories. Cochez, puis préparez le bordereau à envoyer
          aux Archives départementales. Rien n'est détruit avant leur visa.
        </p>
        {libres.length === 0 ? <p className="text-sm text-ink-subtle">Rien d'éliminable pour l'instant.</p> : (
          <>
            <ul className="divide-y divide-border/50">
              {libres.map((u) => (
                <li key={u.id} className="flex items-center gap-2 py-1.5 text-sm">
                  <input type="checkbox" aria-label={`Proposer ${u.intitule}`} checked={choisies.includes(u.id)}
                    onChange={(e) => setChoisies((l) => (e.target.checked ? [...l, u.id] : l.filter((x) => x !== u.id)))} />
                  <span className="text-ink">{u.intitule}</span>
                  <span className="text-2xs text-ink-subtle">{u.categorie ?? '—'} · {dates(u.anneeDebut, u.anneeFin)} · DUA {u.duaAnnees} an(s){u.contenant ? ` · ${u.contenant}` : ''}</span>
                  <span className="ml-auto text-2xs text-ink-subtle">depuis le {fr(u.eliminableDes)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button type="button" className="text-xs text-accent hover:underline"
                onClick={() => setChoisies(choisies.length === libres.length ? [] : libres.map((u) => u.id))}>
                {choisies.length === libres.length ? 'Tout décocher' : 'Tout cocher'}
              </button>
              <Input className="min-w-[200px] flex-1" value={service} maxLength={200} placeholder="Service producteur (facultatif)"
                aria-label="Service producteur" onChange={(e) => setService(e.target.value)} />
              <Button size="sm" variant="primary" disabled={choisies.length === 0}
                onClick={() => void act(async () => {
                  await api.admin.archives.creerBordereau({ uniteIds: choisies, serviceVersant: service.trim() || null });
                  setChoisies([]);
                })}>
                Préparer le bordereau ({choisies.length})
              </Button>
            </div>
          </>
        )}
        {aVenir.length > 0 && (
          <p className="mt-3 text-xs text-ink-subtle">
            {aVenir.length} autre(s) dossier(s) deviendront éliminables dans les 12 prochains mois
            (premier le {fr(aVenir[0]!.eliminableDes)}).
          </p>
        )}
      </Card>

      <Card variant="glass" padded>
        <h2 className="mb-3 text-sm font-semibold text-ink">Bordereaux d'élimination</h2>
        {bordereaux.length === 0 && <p className="text-sm text-ink-subtle">Aucun bordereau.</p>}
        <ul className="space-y-2">
          {bordereaux.map((b) => (
            <li key={b.id} className="rounded-r-md border border-border/60 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <b className="font-mono text-ink">{b.numero}</b>
                <Badge variant={STATUT[b.statut].v} size="sm">{STATUT[b.statut].l}</Badge>
                <span className="text-2xs text-ink-subtle">{b.nbUnites} dossier(s) · {b.metrage.toFixed(2).replace('.', ',')} ml</span>
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => void api.admin.archives.bordereauPdf(b.id, b.numero)}>
                  <FileDown className="h-4 w-4" aria-hidden="true" /> PDF
                </Button>
              </div>
              <p className="mt-1 text-2xs text-ink-subtle">
                {b.dateEnvoi && `Envoyé le ${fr(b.dateEnvoi)}. `}
                {b.visaDate && `Visa du ${fr(b.visaDate)}${b.visaReference ? ` (${b.visaReference})` : ''}. `}
                {b.dateDestruction && `Détruit le ${fr(b.dateDestruction)} — ${b.modeDestruction}. À conserver indéfiniment.`}
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                {b.statut === 'brouillon' && (
                  <Button size="sm" variant="glass" onClick={() => void act(() => api.admin.archives.envoyerBordereau(b.id))}>
                    <Send className="h-4 w-4" aria-hidden="true" /> Marquer envoyé aux AD
                  </Button>
                )}
                {b.statut === 'envoye' && (<>
                  <Input type="date" aria-label="Date du visa" className="w-40" value={saisie[b.id]?.date ?? ''} onChange={(e) => maj(b.id, 'date', e.target.value)} />
                  <Input aria-label="Référence du visa" className="min-w-[160px] flex-1" placeholder="Référence du visa (facultative)"
                    value={saisie[b.id]?.ref ?? ''} onChange={(e) => maj(b.id, 'ref', e.target.value)} />
                  <Button size="sm" variant="primary" disabled={!saisie[b.id]?.date}
                    onClick={() => void act(() => api.admin.archives.visaBordereau(b.id, { visaDate: saisie[b.id]!.date!, visaReference: saisie[b.id]?.ref?.trim() || null }))}>
                    <BadgeCheck className="h-4 w-4" aria-hidden="true" /> Visa reçu
                  </Button>
                </>)}
                {b.statut === 'vise' && (<>
                  <Input type="date" aria-label="Date de destruction" className="w-40" value={saisie[b.id]?.date ?? ''} onChange={(e) => maj(b.id, 'date', e.target.value)} />
                  <Input aria-label="Mode de destruction" className="min-w-[200px] flex-1" placeholder="Ex : broyage par prestataire, certificat n°…"
                    value={saisie[b.id]?.mode ?? ''} onChange={(e) => maj(b.id, 'mode', e.target.value)} />
                  <Button size="sm" variant="primary" disabled={!saisie[b.id]?.date || (saisie[b.id]?.mode?.trim().length ?? 0) < 3}
                    onClick={() => void act(() => api.admin.archives.destructionBordereau(b.id, { dateDestruction: saisie[b.id]!.date!, modeDestruction: saisie[b.id]!.mode!.trim() }))}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" /> Destruction effectuée
                  </Button>
                </>)}
                {(b.statut === 'brouillon' || b.statut === 'envoye') && (
                  <Button size="sm" variant="ghost" onClick={() => void act(() => api.admin.archives.annulerBordereau(b.id))}>
                    <X className="h-4 w-4" aria-hidden="true" /> Annuler
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/** Édition en ligne de la DUA et du sort final d'une catégorie (onglet Catégories). */
export function CategorieDuaEditor({ categorie, onSaved }: {
  categorie: { id: string; duaAnnees?: number | null; sortFinalDefaut?: string | null };
  onSaved: () => void;
}) {
  const [dua, setDua] = useState(categorie.duaAnnees?.toString() ?? '');
  const [sort, setSort] = useState(categorie.sortFinalDefaut ?? '');
  const enregistrer = async (d: string, so: string) => {
    try {
      await api.admin.archives.majCategorie(categorie.id, {
        duaAnnees: d === '' ? null : Math.max(0, Math.min(200, Number(d))), sortFinalDefaut: so || null,
      });
      onSaved();
    } catch { /* l'affichage se recale au rechargement */ }
  };
  return (
    <span className="flex items-center gap-1">
      <Input type="number" min={0} max={200} className="h-8 w-16" aria-label="DUA en années" placeholder="DUA"
        value={dua} onChange={(e) => setDua(e.target.value)} onBlur={() => void enregistrer(dua, sort)} />
      <span className="text-2xs text-ink-subtle">an(s)</span>
      <select aria-label="Sort final par défaut" className="h-8 rounded-r-md border border-border bg-surface px-1 text-2xs"
        value={sort} onChange={(e) => { setSort(e.target.value); void enregistrer(dua, e.target.value); }}>
        <option value="">Sort final…</option>
        <option value="conservation">Conservation</option>
        <option value="elimination">Élimination</option>
        <option value="tri">Tri</option>
        <option value="versement">Versement</option>
      </select>
    </span>
  );
}
